import { useMemo, useState } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { yupResolver } from '@hookform/resolvers/yup';
import DOMPurify from 'dompurify';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import * as yup from 'yup';
import { HttpError } from '@rossum/api-client/errors';
import {
  ExtensionEvent,
  extensionFunctionType,
  extensionWebhookType,
  Hook,
  HookPatchPayload,
  rossumStore,
} from '@rossum/api-client/hooks';
import { Header, HeaderTitle } from '@rossum/components/header/Header';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { IconLibrary, IconLogs, IconTrash } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { useUnpaginatedUsers } from '../../../../business/users/useUnpaginatedUsers';
import { activitiesExtensionsPath } from '../../../../containers/Activities/routes';
import { useLocalStorageBooleanFlag } from '../../../../lib/hooks';
import { sanitizeLinks } from '../../../../lib/htmlSanitization';
import { EXTENSIONS_ADVANCED_SETTINGS_EXPANDED } from '../../../../redux/modules/localStorage/actions';
import { externalStoresEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { HelmetComponent } from '../../../../routes/HelmetComponent';
import { Url } from '../../../../types/basic';
import { HookFormObjectValue } from '../../../../types/CustomTypes/HookFormObject';
import { DeleteExtensionDialog } from '../../components/DeleteExtensionDialog';
import { DuplicateExtensionDialog } from '../../components/DuplicateExtensionDialog';
import { ExtensionsBreadcrumbs } from '../../components/ExtensionsBreadcrumbs';
import {
  EXTENSION_DESCRIPTION_MAX_LENGTH,
  isPublicFunction,
  isPublicWebhook,
  webhookUrlValidation,
} from '../../helpers';
import { useHook } from '../../hooks/useHook';
import { usePatchHook } from '../../hooks/usePatchHook';
import { getIcon, validateCronString } from '../../lib/helpers';
import { useJSONfield } from '../../lib/useJSONfield';
import { myExtensionsPath } from '../../routes';
import { AdvancedSettings } from '../create-extension/components/AdvancedSettings';
import { UpdateButton } from '../external-store/components/UpdateButton';
import { ExtensionHeaderActions } from './components/ExtensionHeaderActions';
import { RuntimeChip } from './components/RuntimeChip';
import { ViewCodePart } from './components/ViewCodePart';
import { GeneralDetailSettings } from './GeneralDetailSettings';
import {
  ExtensionData,
  getDefaultValues,
  getSecretsWithPlaceholders,
  NO_CHANGE_SECRET_PLACEHOLDER,
} from './helpers';

DOMPurify.addHook('afterSanitizeAttributes', sanitizeLinks);

const SAVE_EXTENSION_FORM_ID = 'save-extension-form';

const sortQueues = (queues: Array<Url>) => R.sortBy(queues, queue => queue);

const validationSchema = (intl: IntlShape) =>
  yup.object().shape({
    active: yup.boolean().required(),
    description: yup
      .string()
      .max(
        EXTENSION_DESCRIPTION_MAX_LENGTH,
        intl.formatMessage(
          {
            id: 'containers.settings.extensions.createExtension.description.tooLong',
          },
          { limit: EXTENSION_DESCRIPTION_MAX_LENGTH }
        )
      )
      .notRequired()
      .nullable(),
    events: yup.array().required(
      intl.formatMessage({
        id: 'containers.settings.extensions.createExtension.events.required',
      })
    ),
    name: yup.string().required(
      intl.formatMessage({
        id: 'containers.settings.extensions.createExtension.name.required',
      })
    ),
    queues: yup.array(),
    integrationCredentials: yup.array(),
    type: yup.string().required('required'),
    secret: yup.string().nullable().notRequired(),
    url: yup.string().when('type', {
      is: extensionWebhookType,
      then: yup.string().when(['private'], {
        is: true,
        then: yup.string().nullable().notRequired(),
        otherwise: webhookUrlValidation(intl),
      }),
      otherwise: yup.string().nullable().notRequired(),
    }),
    sideload: yup.array(),
    tokenOwner: yup.string().nullable(),
    settings: yup.object(),
    runtime: yup.string(),
    schedule: yup
      .string()
      .when('events', (events: ExtensionEvent[], schema: yup.StringSchema) =>
        events.includes('invocation.scheduled')
          ? schema.test(
              'wrongFormat',
              intl.formatMessage({
                id: 'containers.settings.extensions.createExtension.schedule.wrongFormat',
              }),
              validateCronString
            )
          : schema
      ),
  });

type ExtensionDetailProps = {
  detailUrl: string;
  selectedExtension: Hook;
};

const ExtensionDetail = ({
  selectedExtension,
  detailUrl,
}: ExtensionDetailProps) => {
  const isFromStore = selectedExtension.extensionSource === rossumStore;
  const [showAdvancedSettings, setShowAdvancedSettings] =
    useLocalStorageBooleanFlag(EXTENSIONS_ADVANCED_SETTINGS_EXPANDED, false);
  const intl = useIntl();

  const { push } = useHistory<{
    backLink?: string;
  }>();

  const { data: organizationUsers } = useUnpaginatedUsers({
    deleted: false,
  });

  const pickChangedSecrets = (secrets: HookFormObjectValue | undefined) =>
    R.pickBy(
      secrets || {},
      secretValue => secretValue !== NO_CHANGE_SECRET_PLACEHOLDER
    );

  const secretsSchemaProperties =
    selectedExtension.secretsSchema?.['properties'];
  const settingsSchemaProperties =
    selectedExtension.settingsSchema?.['properties'];

  const secretsWithPlaceholders = useMemo(
    () =>
      getSecretsWithPlaceholders({
        secretsSchemaProperties,
        settingsSchemaProperties,
      }),
    [secretsSchemaProperties, settingsSchemaProperties]
  );

  const extensionIsPublicFunction = isPublicFunction(selectedExtension);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    setError,
    trigger,
  } = useForm<ExtensionData>({
    mode: 'onTouched',
    defaultValues: getDefaultValues(selectedExtension),
    resolver: yupResolver(validationSchema(intl)),
  });
  const watchFields = watch();
  const { events } = watchFields;

  const isWebhookPublic = isPublicWebhook(selectedExtension);

  const useFunctionCode = isPublicFunction(selectedExtension);

  const noChanges = R.isDeepEqual(
    {
      ...watchFields,
      description: watchFields.description || '',
      queues: sortQueues(watchFields.queues),
      secret: watchFields.secret || '',
    },
    {
      schedule: selectedExtension.config.schedule?.cron ?? '',
      active: selectedExtension.active,
      type: selectedExtension.type,
      name: selectedExtension.name,
      description: selectedExtension.description || '',
      events: selectedExtension.events,
      queues: sortQueues(selectedExtension.queues),
      integrationCredentials: selectedExtension.integrationCredentials ?? [],
      url: (isWebhookPublic && selectedExtension.config.url) || '',
      secret: (isWebhookPublic && selectedExtension.config.secret) || '',
      tokenOwner: selectedExtension.tokenOwner,
      settings: selectedExtension.settings,
      sideload: selectedExtension.sideload,
      secrets: secretsWithPlaceholders,
      private: selectedExtension.config.private,
      runtime: useFunctionCode ? selectedExtension.config.runtime : undefined,
      payloadLoggingEnabled:
        selectedExtension.config.payloadLoggingEnabled ?? false,
    }
  );

  const { mutate, isLoading } = usePatchHook();

  const [contextAction, setContextAction] = useState<
    'delete' | 'duplicate' | undefined
  >(undefined);

  const onSubmit = (mutateOptions?: { onSuccess: () => void }) =>
    handleSubmit(
      ({
        url,
        secret,
        secrets,
        queues,
        schedule,
        runtime,
        payloadLoggingEnabled,
        ...data
      }: ExtensionData) => {
        const scheduleConfig = schedule ? { schedule: { cron: schedule } } : {};
        const runtimeConfig = extensionIsPublicFunction ? { runtime } : {};

        const extension = {
          ...data,
          queues,
          settingsSchema: data.settingsSchema as Record<string, unknown>,
          secretsSchema: data.secretsSchema as Record<string, unknown>,
          secrets: pickChangedSecrets(secrets) as Record<string, unknown>,
          ...(isWebhookPublic
            ? {
                config: {
                  url,
                  secret: secret || null,
                  payloadLoggingEnabled,
                  ...scheduleConfig,
                },
              }
            : {
                config: {
                  payloadLoggingEnabled,
                  ...scheduleConfig,
                  ...runtimeConfig,
                },
              }),
        };

        return mutate(
          {
            hookId: selectedExtension.id,
            payload: extension,
            meta: {
              withMessage:
                selectedExtension.type === 'function'
                  ? // do not display message when user is retrying to save failed function
                    selectedExtension.status !== 'failed'
                  : true,
            },
          },
          mutateOptions
        );
      }
    );

  const updateExtensionDetail = (payload: HookPatchPayload) =>
    mutate({
      hookId: selectedExtension.id,
      payload,
      meta: { withMessage: false },
    });

  const settingsJSONField = useJSONfield<ExtensionData>({
    name: 'settings',
    trigger,
    setError,
    control,
    defaultValue: selectedExtension.settings as Record<string, object>,
    saveField: (name, value) =>
      updateExtensionDetail({ [name]: value, type: selectedExtension.type }),
    schema: selectedExtension.settingsSchema,
  });

  const secretsJSONField = useJSONfield<ExtensionData>({
    name: 'secrets',
    trigger,
    setError,
    control,
    schema: selectedExtension.secretsSchema,
  });

  const showSecretsSection = !(
    isFromStore && !selectedExtension.secretsSchema?.['properties']
  );

  const externalStoresEnabled = useSelector(externalStoresEnabledSelector);

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          {...params}
          title={hasScrolled => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <HeaderTitle
                hasScrolled={hasScrolled}
                title={selectedExtension.name}
              />
              {selectedExtension.type === extensionFunctionType && (
                <Box sx={{ svg: { display: 'block' } }}>
                  {getIcon(
                    get(selectedExtension, 'type'),
                    get(selectedExtension, ['config', 'runtime']),
                    { fontSize: 'large' }
                  )}
                </Box>
              )}
              {extensionIsPublicFunction && (
                <RuntimeChip runtime={selectedExtension.config.runtime} />
              )}
              {isFromStore && (
                <Chip
                  data-cy="from-store-tag"
                  label={intl.formatMessage({
                    id: 'containers.settings.extensions.title.fromStore.tag',
                  })}
                  color="info"
                />
              )}
            </Stack>
          )}
          description={intl.formatMessage({
            id: `containers.settings.extensions.title.${
              isFromStore ? 'fromStore' : selectedExtension.type
            }`,
          })}
          breadcrumbs={
            <ExtensionsBreadcrumbs
              breadcrumbs={[
                {
                  to: myExtensionsPath(),
                  label: intl.formatMessage({
                    id: 'features.routes.pageTitles.extensions',
                  }),
                },
                {
                  label: selectedExtension.name,
                },
              ]}
            />
          }
          buttons={[
            <ExtensionHeaderActions
              key="extension-header-actions"
              selectedExtension={selectedExtension}
              isValid={isValid}
              onSubmit={onSubmit()}
              control={control}
            />,
            ...(externalStoresEnabled
              ? [
                  <UpdateButton
                    selectedExtension={selectedExtension}
                    key="update-button"
                  />,
                ]
              : []),
            <Tooltip
              key="activities-extension-button"
              placement="top"
              disableInteractive
              title={intl.formatMessage({
                id: 'containers.settings.extensions.actionButtons.activities',
              })}
            >
              <IconButton
                component={RouterLink}
                to={`${activitiesExtensionsPath()}?hook=${selectedExtension.id}`}
                data-cy="extensions-activities-button"
              >
                <SvgIcon fontSize="large">
                  <IconLogs />
                </SvgIcon>
              </IconButton>
            </Tooltip>,
            <Tooltip
              key="delete-extension-button"
              placement="top"
              disableInteractive
              title={intl.formatMessage({
                id: 'containers.settings.extensions.actionButtons.delete',
              })}
            >
              <IconButton
                disabled={get(selectedExtension, 'status') === 'pending'}
                onClick={() => setContextAction('delete')}
                data-cy="extensions-delete-extension"
              >
                <SvgIcon fontSize="large">
                  <IconTrash />
                </SvgIcon>
              </IconButton>
            </Tooltip>,
            <Tooltip
              key="duplicate-extension-button"
              placement="top"
              disableInteractive
              title={intl.formatMessage({
                id: 'containers.settings.extensions.actionButtons.duplicate',
              })}
            >
              <IconButton onClick={() => setContextAction('duplicate')}>
                <SvgIcon fontSize="large">
                  <IconLibrary />
                </SvgIcon>
              </IconButton>
            </Tooltip>,
            <Button
              key="save-extension-button"
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} /> : null}
              disabled={
                get(selectedExtension, 'status') === 'pending' ||
                !isValid ||
                isLoading ||
                noChanges
              }
              data-cy={`extensions-${selectedExtension.type}-confirm-button`}
              type="submit"
              form={SAVE_EXTENSION_FORM_ID}
            >
              {intl.formatMessage({
                id: 'containers.settings.queues.save',
              })}
            </Button>,
          ]}
        />
      )}
    >
      <HelmetComponent
        dynamicName={selectedExtension.name}
        translationKey="features.routes.pageTitles.extensions.edit"
      />
      <Stack py={4}>
        <Paper
          sx={{ px: 30, py: 4 }}
          component="form"
          id={SAVE_EXTENSION_FORM_ID}
          onSubmit={onSubmit({
            onSuccess: () => {
              secretsJSONField.onChange(
                JSON.stringify(secretsWithPlaceholders, null, 2)
              );
            },
          })}
        >
          <Stack spacing={4}>
            <GeneralDetailSettings
              selectedExtension={selectedExtension}
              detailUrl={detailUrl}
              control={control}
              events={events}
              noChanges={noChanges}
              eventsHasError={!!errors.events}
            />
            <AdvancedSettings<ExtensionData>
              control={control}
              allOrgUsers={organizationUsers ?? []}
              showAdvancedSettings={showAdvancedSettings}
              setShowAdvancedSettings={setShowAdvancedSettings}
              users={organizationUsers ?? []}
              settingsJSONField={settingsJSONField}
              secretsJSONField={secretsJSONField}
              isFromStore={isFromStore}
              readMoreUrl={selectedExtension.readMoreUrl}
              name={selectedExtension.name}
              selectedExtension={selectedExtension}
              showSecretsSection={showSecretsSection}
            />
            {isFromStore && (
              <ViewCodePart
                selectedExtension={selectedExtension}
                isFromStore={isFromStore}
                routeUrl={detailUrl}
              />
            )}
            <LeavingDialog when={!noChanges} />
          </Stack>
        </Paper>
      </Stack>

      <DeleteExtensionDialog
        open={contextAction === 'delete'}
        onClose={() => setContextAction(undefined)}
        onDelete={() => push({ pathname: myExtensionsPath() })}
        extension={selectedExtension}
      />
      <DuplicateExtensionDialog
        open={contextAction === 'duplicate'}
        onClose={() => setContextAction(undefined)}
        extension={selectedExtension}
      />
    </PageLayout>
  );
};

type ExtensionDetailPageProps = {
  extensionId: number;
  detailUrl: string;
};

export const ExtensionDetailPage = (props: ExtensionDetailPageProps) => {
  const { extensionId, detailUrl } = props;

  const {
    data: selectedExtension,
    error,
    isLoading,
  } = useHook(extensionId, {
    refetchInterval: hook =>
      hook && hook.type === 'function' && hook.status === 'pending'
        ? 10000
        : false,
  });

  if (error instanceof HttpError && error.code === 404) {
    return <Redirect to={myExtensionsPath()} />;
  }

  if (isLoading || !selectedExtension) {
    return <LinearProgress />;
  }

  return (
    <ExtensionDetail
      selectedExtension={selectedExtension}
      detailUrl={detailUrl}
    />
  );
};
